import type { Landmark } from '../types';
import { IDX } from '../constants';
import { getEvaluationRules } from '@/lib/posture-analysis/evaluation-rules-load';

type PosteriorHipIliacBiasCfg = {
  iliacWidthRatio: number;
  medialTriggerRatio: number;
  yLerpWhenLower: number;
};

const POSTERIOR_HIP_ILIAC_BIAS_FALLBACK: PosteriorHipIliacBiasCfg = {
  iliacWidthRatio: 0.78,
  medialTriggerRatio: 0.78,
  yLerpWhenLower: 0.75,
};

export function getPosteriorHipIliacBiasConfig(): PosteriorHipIliacBiasCfg {
  const raw = (getEvaluationRules().posture as { posteriorHipIliacBias?: Partial<PosteriorHipIliacBiasCfg> } | undefined)
    ?.posteriorHipIliacBias;
  const num = (v: unknown, fallback: number): number =>
    typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback;
  return {
    iliacWidthRatio: num(raw?.iliacWidthRatio, POSTERIOR_HIP_ILIAC_BIAS_FALLBACK.iliacWidthRatio),
    medialTriggerRatio: num(raw?.medialTriggerRatio, POSTERIOR_HIP_ILIAC_BIAS_FALLBACK.medialTriggerRatio),
    yLerpWhenLower: num(raw?.yLerpWhenLower, POSTERIOR_HIP_ILIAC_BIAS_FALLBACK.yLerpWhenLower),
  };
}

export const POSTERIOR_HIP_ILIAC_WIDTH_RATIO = POSTERIOR_HIP_ILIAC_BIAS_FALLBACK.iliacWidthRatio;
export const POSTERIOR_HIP_MEDIAL_TRIGGER_RATIO = POSTERIOR_HIP_ILIAC_BIAS_FALLBACK.medialTriggerRatio;
export const POSTERIOR_HIP_Y_LERP_WHEN_LOWER = POSTERIOR_HIP_ILIAC_BIAS_FALLBACK.yLerpWhenLower;

type PelvisPinOverrideCfg = {
  maxAbsDeg: number;
  maxDisagreeFromHipDeg: number;
  ignoreWhenHipAbsDeg: number;
};

const PELVIS_PIN_OVERRIDE_FALLBACK: PelvisPinOverrideCfg = {
  maxAbsDeg: 12,
  maxDisagreeFromHipDeg: 1.5,
  ignoreWhenHipAbsDeg: 1.5,
};

export function getPelvisPinOverrideConfig(): PelvisPinOverrideCfg {
  const raw = (getEvaluationRules().posture as { pelvisPins?: Partial<PelvisPinOverrideCfg> } | undefined)?.pelvisPins;
  const num = (v: unknown, fallback: number): number =>
    typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback;
  return {
    maxAbsDeg: num(raw?.maxAbsDeg, PELVIS_PIN_OVERRIDE_FALLBACK.maxAbsDeg),
    maxDisagreeFromHipDeg: num(raw?.maxDisagreeFromHipDeg, PELVIS_PIN_OVERRIDE_FALLBACK.maxDisagreeFromHipDeg),
    ignoreWhenHipAbsDeg: num(raw?.ignoreWhenHipAbsDeg, PELVIS_PIN_OVERRIDE_FALLBACK.ignoreWhenHipAbsDeg),
  };
}

/**
 * True when ASIS/PSIS should replace hip-based pelvis level.
 * Degenerate, hip-disagreed, or stacked-hip pin lines (common on stored inferred pins) fall back to hips.
 */
export function pelvisPinsOverrideHip(pinDeg: number, hipDeg: number): boolean {
  if (!Number.isFinite(pinDeg)) return false;
  const { maxAbsDeg, maxDisagreeFromHipDeg, ignoreWhenHipAbsDeg } = getPelvisPinOverrideConfig();
  if (Math.abs(pinDeg) > maxAbsDeg) return false;
  if (!Number.isFinite(hipDeg)) return true;
  if (Math.abs(hipDeg) <= ignoreWhenHipAbsDeg) return false;
  return Math.abs(pinDeg - hipDeg) <= maxDisagreeFromHipDeg;
}

/**
 * Fraction of the horizontal distance from each wrist toward shoulder midline (Anterior/Posterior).
 * BlazePose wrists often sit slightly ulnar/lateral; this nudges them toward the torso for easier editing.
 */
/** Anterior-only; keep small to avoid visible lateral drift. */
export const WRIST_TOWARD_SHOULDER_MIDLINE_LERP = 0.04;

/**
 * Fraction of the vector from each ankle toward its heel used after pose detection.
 * BlazePose ankles often sit slightly forward of the heel; blending improves plumb-line metrics.
 */
export const ANKLE_HEEL_BLEND_LERP = 0.55;

/** Mutates ankle landmarks in place toward heel. No-op if ankle or heel missing. */
export function applyAnkleHeelBlend(landmarks: Landmark[]): void {
  const pairs: [number, number][] = [
    [IDX.L_ANK, IDX.L_HEEL],
    [IDX.R_ANK, IDX.R_HEEL],
  ];
  const t = ANKLE_HEEL_BLEND_LERP;

  for (const [ankIdx, heelIdx] of pairs) {
    const ankle = landmarks[ankIdx];
    const heel = landmarks[heelIdx];
    if (!ankle || !heel) continue;

    ankle.x += (heel.x - ankle.x) * t;
    ankle.y += (heel.y - ankle.y) * t;
  }
}

/** Mutates landmarks in place. No-op if wrists missing. */
export function applyFrontalWristTowardMidline(landmarks: Landmark[], imageWidthPx: number): void {
  const lW = landmarks[IDX.L_WR];
  const rW = landmarks[IDX.R_WR];
  if (!lW || !rW) return;

  const lS = landmarks[IDX.L_SHO];
  const rS = landmarks[IDX.R_SHO];
  const midX = lS && rS ? (lS.x + rS.x) / 2 : imageWidthPx / 2;
  const t = WRIST_TOWARD_SHOULDER_MIDLINE_LERP;

  lW.x += (midX - lW.x) * t;
  rW.x += (midX - rW.x) * t;
}

export type FrontalHipIliacBiasView = 'Anterior' | 'Posterior';

/**
 * BlazePose hips (23/24) often sit too medial / low vs the iliac outline
 * (posterior: glute mass; anterior: the same medial drift). That inflates
 * pelvis-level and can disagree across A/P.
 *
 * Ratios: `posture.posteriorHipIliacBias` in evaluation-rules.yaml.
 * Mutates hips in place. Idempotent: after a snap, |hip−mid| equals
 * `iliacWidthRatio × shoulderHalf` (trigger is strict <), so a second call is a no-op.
 * No-op if shoulders/hips are missing or the shoulder span is degenerate.
 *
 * Anatomical left is image-left on Posterior (facing away) and image-right on
 * Anterior (facing the camera). Snap X is mirrored for Anterior so L/R are not swapped.
 */
export function applyFrontalHipIliacBias(
  landmarks: Landmark[],
  view: FrontalHipIliacBiasView,
): void {
  const lSho = landmarks[IDX.L_SHO];
  const rSho = landmarks[IDX.R_SHO];
  const lHip = landmarks[IDX.L_HIP];
  const rHip = landmarks[IDX.R_HIP];
  if (!lSho || !rSho || !lHip || !rHip) return;

  const midX = (lSho.x + rSho.x) / 2;
  const shoHalf = Math.abs(rSho.x - lSho.x) / 2;
  if (!Number.isFinite(shoHalf) || shoHalf < 10) return;

  const { iliacWidthRatio, medialTriggerRatio, yLerpWhenLower } = getPosteriorHipIliacBiasConfig();
  const iliacHalf = iliacWidthRatio * shoHalf;
  const medialLimit = medialTriggerRatio * shoHalf;
  const anterior = view === 'Anterior';

  const needsFix = (hip: Landmark, expectedLeftOfMid: boolean): boolean => {
    const fromMid = hip.x - midX;
    const wrongSide = expectedLeftOfMid ? fromMid > 0 : fromMid < 0;
    const tooMedial = Math.abs(fromMid) < medialLimit;
    return tooMedial || wrongSide;
  };

  const fixL = needsFix(lHip, !anterior);
  const fixR = needsFix(rHip, anterior);

  if (fixL) lHip.x = anterior ? midX + iliacHalf : midX - iliacHalf;
  if (fixR) rHip.x = anterior ? midX - iliacHalf : midX + iliacHalf;

  // Y-correct any fixed hip that is lower — including when both hips snap.
  // Gating on "sole medial outlier" left bilateral-narrow + unilateral-drop
  // cases (EinJdc) at the raw 4.8° Y obliquity.
  if (fixL && lHip.y > rHip.y) {
    lHip.y += (rHip.y - lHip.y) * yLerpWhenLower;
  } else if (fixR && rHip.y > lHip.y) {
    rHip.y += (lHip.y - rHip.y) * yLerpWhenLower;
  }
}

/** Posterior-facing wrapper. Prefer `applyFrontalHipIliacBias` for new call sites. */
export function applyPosteriorHipIliacBias(landmarks: Landmark[]): void {
  applyFrontalHipIliacBias(landmarks, 'Posterior');
}

type FrontalShoulderTowardHeadBiasCfg = {
  yLerp: number;
  disagreeTriggerAbsDeg: number;
  stackedAbsDeg: number;
};

const FRONTAL_SHOULDER_TOWARD_HEAD_BIAS_FALLBACK: FrontalShoulderTowardHeadBiasCfg = {
  yLerp: 1,
  disagreeTriggerAbsDeg: 2,
  stackedAbsDeg: 1.5,
};

export function getFrontalShoulderTowardHeadBiasConfig(): FrontalShoulderTowardHeadBiasCfg {
  const raw = (getEvaluationRules().posture as { frontalShoulderTowardHeadBias?: Partial<FrontalShoulderTowardHeadBiasCfg> } | undefined)
    ?.frontalShoulderTowardHeadBias;
  const numPos = (v: unknown, fallback: number): number =>
    typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback;
  const lerp = (v: unknown, fallback: number): number =>
    typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1 ? v : fallback;
  return {
    yLerp: lerp(raw?.yLerp, FRONTAL_SHOULDER_TOWARD_HEAD_BIAS_FALLBACK.yLerp),
    disagreeTriggerAbsDeg: numPos(
      raw?.disagreeTriggerAbsDeg,
      FRONTAL_SHOULDER_TOWARD_HEAD_BIAS_FALLBACK.disagreeTriggerAbsDeg,
    ),
    stackedAbsDeg: numPos(raw?.stackedAbsDeg, FRONTAL_SHOULDER_TOWARD_HEAD_BIAS_FALLBACK.stackedAbsDeg),
  };
}

export const FRONTAL_SHOULDER_TOWARD_HEAD_Y_LERP = FRONTAL_SHOULDER_TOWARD_HEAD_BIAS_FALLBACK.yLerp;
export const FRONTAL_SHOULDER_DISAGREE_TRIGGER_ABS_DEG =
  FRONTAL_SHOULDER_TOWARD_HEAD_BIAS_FALLBACK.disagreeTriggerAbsDeg;
export const FRONTAL_SHOULDER_STACKED_ABS_DEG = FRONTAL_SHOULDER_TOWARD_HEAD_BIAS_FALLBACK.stackedAbsDeg;

const rad2deg = (r: number) => (r * 180) / Math.PI;

/**
 * BlazePose shoulders 11/12 often drop an acromion onto deltoid/trap, so
 * `Shoulders level` disagrees with the ear line and across A/P while the head
 * stays stacked. Mutates shoulder Y in place toward the ear-line obliquity.
 *
 * Gates (YAML `posture.frontalShoulderTowardHeadBias`):
 * - no-op when ears/shoulders missing or the span is degenerate
 * - no-op when the ear line is not stacked (do not follow a tilted head across A/P)
 * - no-op when `|shoulderDeg − headDeg|` is already within `disagreeTriggerAbsDeg`
 * - no-op when `|shoulderDeg|` is already stacked (do not manufacture tilt)
 *
 * Compensation (shoulder vs head opposite and unstacked) is skipped because the
 * ear line must already be stacked. Does not read the other capture view.
 */
export function applyFrontalShoulderTowardHeadBias(landmarks: Landmark[]): void {
  const lE = landmarks[IDX.L_EAR];
  const rE = landmarks[IDX.R_EAR];
  const lS = landmarks[IDX.L_SHO];
  const rS = landmarks[IDX.R_SHO];
  if (!lE || !rE || !lS || !rS) return;

  const earWidth = Math.abs(rE.x - lE.x);
  const shoWidth = Math.abs(rS.x - lS.x);
  if (!Number.isFinite(earWidth) || earWidth < 10) return;
  if (!Number.isFinite(shoWidth) || shoWidth < 10) return;

  const { yLerp, disagreeTriggerAbsDeg, stackedAbsDeg } = getFrontalShoulderTowardHeadBiasConfig();
  const headDeg = rad2deg(Math.atan2(rE.y - lE.y, earWidth));
  const shoDeg = rad2deg(Math.atan2(rS.y - lS.y, shoWidth));
  if (!Number.isFinite(headDeg) || !Number.isFinite(shoDeg)) return;
  if (Math.abs(headDeg) >= stackedAbsDeg) return;
  if (Math.abs(shoDeg - headDeg) <= disagreeTriggerAbsDeg) return;
  if (Math.abs(shoDeg) < stackedAbsDeg) return;

  const targetDy = Math.tan((headDeg * Math.PI) / 180) * shoWidth;
  const currentDy = rS.y - lS.y;
  const newDy = currentDy + (targetDy - currentDy) * yLerp;
  const delta = newDy - currentDy;
  lS.y -= delta / 2;
  rS.y += delta / 2;
}
